import {
  type PropsWithChildren,
  use,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { ArrowLeft } from "../Icon/ArrowLeft";
import {
  Header as HeaderLayout,
  Heading,
  IconAction,
  type IconActionProps,
} from "../_shared/Header/Header";
import * as headerStyles from "../_shared/Header/Header.module.scss";
import * as styles from "./Wizard.module.scss";
import {
  type WizardActions,
  WizardActionsContext,
  type WizardState,
  WizardStateContext,
  WizardStepMetaContext,
  type WizardDirection,
} from "./WizardContext";
import { useWizard } from "./hooks/useWizard";
import { useFrozenWhileExiting } from "./hooks/useFrozenWhileExiting";
import { wizardReducer } from "./wizardReducer";
import { AnimatePresence, motion } from "motion/react";
import { SmoothHeightTransition } from "../SmoothHeightTransition/SmoothHeightTransition";

/**
 * Headless multi-step navigation container. Owns step registry, history, and
 * transitions. Can be composed inside Modal or inline in a page.
 *
 * See full spec including decisions and component tree at ./README.md
 */

export interface WizardStepProps extends PropsWithChildren {
  name: string;
  title?: string;
  subtitle?: string;
}

const StepSubtitle = () => {
  const { activeStepMeta } = useWizard();

  return <span style={{ display: "block" }}>{activeStepMeta?.subtitle}</span>;
};

type BackActionProps = Omit<IconActionProps, "label">;

const BackAction = ({ children, onClick, ...props }: BackActionProps) => {
  const { canGoBack: liveCanGoBack, back } = useWizard();
  // inside an exiting pane, canGoBack has already flipped to the incoming
  // step's value — keep the value this pane had while it was active
  const canGoBack = useFrozenWhileExiting(liveCanGoBack);

  if (!canGoBack) return null;

  return (
    <IconAction
      aria-label="Go back"
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          back();
        }
      }}
      {...props}
    >
      {children ?? <ArrowLeft />}
    </IconAction>
  );
};

const StepTitle = () => {
  const { activeStepMeta } = useWizard();

  return <span style={{ display: "block" }}>{activeStepMeta?.title}</span>;
};

export interface WizardHeaderProps extends PropsWithChildren {
  /**
   * Terminal steps are reached with `replace` and must not be navigated back
   * out of, but `canGoBack` falls back to the linear step order, so the arrow
   * has to be suppressed explicitly. Defaults to true.
   */
  backAction?: boolean;
  /**
   * Renders the back arrow inert rather than removing it, so a step that
   * blocks navigation mid-flight doesn't reflow its header. Defaults to false.
   */
  backDisabled?: boolean;
}

const Header = ({
  children,
  backAction = true,
  backDisabled = false,
}: WizardHeaderProps) => {
  const { activeStepMeta } = useWizard();
  // prefer the enclosing step's own meta so an exiting pane keeps its title;
  // fall back to active meta when composed outside a step
  const stepMeta = use(WizardStepMetaContext);
  const meta = stepMeta ?? activeStepMeta;

  return (
    <HeaderLayout>
      {backAction && <BackAction disabled={backDisabled} />}
      <Heading>
        {meta?.title && <h2 className={headerStyles.title}>{meta.title}</h2>}
        {meta?.subtitle && (
          <p className={headerStyles.subtitle}>{meta.subtitle}</p>
        )}
      </Heading>
      {children}
    </HeaderLayout>
  );
};

// one spring for the pane slide and the container height so they land
// together
const WIZARD_SPRING = { type: "spring", bounce: 0.1, duration: 0.4 } as const;

const stepVariants = {
  enter: (direction: WizardDirection) => ({
    x: direction === "forward" ? "100%" : "-100%",
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: WizardDirection) => ({
    x: direction === "forward" ? "-100%" : "100%",
    opacity: 0,
  }),
};

const Step = ({ name, title, subtitle, children }: WizardStepProps) => {
  const { registerStep, activeStep, direction } = useWizard();

  useEffect(() => {
    const unregisterStep = registerStep({ name, title, subtitle });

    return () => {
      unregisterStep();
    };
  }, []);

  const stepMeta = useMemo(
    () => ({ name, title, subtitle }),
    [name, title, subtitle],
  );

  const isStepActive = activeStep === name;

  // The step showing when the wizard first mounts shouldn't slide in. Putting
  // initial={false} on AnimatePresence instead causes every motion element
  // inside the step to lose its mount animation too.
  const isFirstRender = useRef(true);
  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return (
    <AnimatePresence custom={direction} mode="popLayout">
      {isStepActive && (
        <motion.div
          key={name}
          custom={direction}
          className={styles.step}
          variants={stepVariants}
          initial={isFirstRender.current ? false : "enter"}
          animate="center"
          exit="exit"
          transition={{
            ...WIZARD_SPRING,
            opacity: { duration: 0.1, ease: "easeOut" },
          }}
        >
          <WizardStepMetaContext value={stepMeta}>
            {children}
          </WizardStepMetaContext>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export interface WizardRootProps extends PropsWithChildren {
  initialStep: string;
  step?: string;
  onStepChange?: (step: string) => void;
}

const Root = ({
  children,
  initialStep,
  step,
  onStepChange,
}: WizardRootProps) => {
  const [state, dispatch] = useReducer(wizardReducer, {
    initialStep,
    activeStep: step ?? initialStep,
    steps: [],
    history: [],
    direction: "forward",
  });

  // read onStepChange through a ref so the notify effect doesn't re-fire when
  // a parent passes a new inline callback every render
  const onStepChangeRef = useRef(onStepChange);
  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  });

  // sync external step changes into the wizard. Only reacts to actual prop
  // changes and skips props vs state changes as that would break internal
  // updates and cause infinite loops
  const prevStepPropRef = useRef(step);
  useEffect(() => {
    const prevStepProp = prevStepPropRef.current;
    prevStepPropRef.current = step;

    if (step === undefined) return; // uncontrolled
    if (step === prevStepProp) return; // prop didn't change; internal render
    if (step === state.activeStep) return; // parent echoing our own change

    dispatch({ type: "sync", step });
  }, [step, state.activeStep]);

  // store the previously notified step to avoid echoing the same step
  const notifiedStepRef = useRef(state.activeStep);

  // report internal navigation to the parent
  useEffect(() => {
    // don't echo the step change if it's the same as the last notified one
    if (notifiedStepRef.current === state.activeStep) return;

    notifiedStepRef.current = state.activeStep;

    // change came from the controlled prop, don't echo it back
    if (step === state.activeStep) return;

    onStepChangeRef.current?.(state.activeStep);
  }, [step, state.activeStep]);

  const actions = useMemo<WizardActions>(
    () => ({
      push: (step) => dispatch({ type: "goTo", step }),
      goTo: (step, options) =>
        dispatch({ type: "goTo", step, replace: options?.replace }),
      back: () => dispatch({ type: "back" }),
      next: () => dispatch({ type: "next" }),
      prev: () => dispatch({ type: "prev" }),
      reset: () => dispatch({ type: "reset" }),
      registerStep: (meta) => {
        dispatch({ type: "register", meta });

        return () => {
          dispatch({ type: "unregister", name: meta.name });
        };
      },
    }),
    [],
  );

  const contextState = useMemo<WizardState>(() => {
    const index = state.steps.findIndex((s) => s.name === state.activeStep);
    return {
      activeStep: state.activeStep,
      steps: state.steps.map((s) => s.name),
      canGoBack: state.history.length > 0 || index > 0,
      direction: state.direction,
      activeStepMeta: state.steps[index],
    };
  }, [state]);

  return (
    <WizardActionsContext value={actions}>
      <WizardStateContext value={contextState}>
        <SmoothHeightTransition transition={WIZARD_SPRING}>
          <div className={styles.root}>{children}</div>
        </SmoothHeightTransition>
      </WizardStateContext>
    </WizardActionsContext>
  );
};

export const Wizard = {
  Root,
  Step,
  Header,
  BackAction,
  StepTitle,
  StepSubtitle,
};
